import React, { useContext, useCallback } from 'react';
import { useSocketEvent } from '../context/SocketContext';
import { AppContext } from '../window';
import { SocketEvent } from '../models/Socket';
import { getLastTransaction } from '../api/userApi';
import { usePaymentSuccess } from '../context/PaymentSuccessContext';

export interface WalletUpdateEvent {
  credits: number;
  freeMessages?: number;
  transaction: {
    amount: number;
    currencyIso: string;
    description: string;
  };
}

const UserSocketManager: React.FC = () => {
  const appContext = useContext(AppContext);
  const { showPaymentSuccess } = usePaymentSuccess();

  // Handle wallet update events
  const handleWalletUpdate = useCallback(async (event: SocketEvent<WalletUpdateEvent>) => {
    const { credits, freeMessages, transaction } = event.data;
    
    // Update the user's wallet credits in app context
    appContext.setUser(prevUser => 
      prevUser ? {
        ...prevUser,
        wallet: {
          ...prevUser.wallet,
          credits: credits,
          // Update freeMessages if provided
          ...(freeMessages !== undefined && { freeMessages })
        }
      } : prevUser
    );

    // Show payment success modal instead of notification
    showPaymentSuccess(transaction.amount, 'success');

    // Fetch the updated lastTransaction
    try {
      const response = await getLastTransaction();
      if (response.success && response.lastTransaction) {
        // Update the user's credentials with the new lastTransaction
        appContext.setUser(prevUser => 
          prevUser ? {
            ...prevUser,
            credentials: {
              ...prevUser.credentials,
              lastTransaction: response.lastTransaction
            }
          } : prevUser
        );
      }
    } catch (error) {
      console.error('Failed to update lastTransaction after wallet update:', error);
    }
  }, [appContext, showPaymentSuccess]);

  // Subscribe to wallet update events
  useSocketEvent<WalletUpdateEvent>('event/wallet-update', handleWalletUpdate);

  // This component doesn't render anything
  return null;
};

export default UserSocketManager;